import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterModel, IServerSideGetRowsParams, SortModelItem } from 'ag-grid-community';
import {
  convertToFilterString,
  convertToOrderByString,
  CustomFilterRule,
  CustomSortRule,
  SetFilterRule,
} from '@workspace-app/shared';
import { CertifiableSystem } from '@workspace-app/odatabackend/QualicoatBackendModel';

// pCode lives on the CoatingSystem derived type, so filtering/sorting it on the base
// CertifiableSystems set requires an OData type-cast segment. Non-coating rows resolve to
// null for this path and are simply excluded by the filter.
const PCODE_CAST_PATH = 'QualicoatBackend.CoatingSystem/pCode';

@Injectable({
  providedIn: 'root',
})
export class ApprovedSystemsDataService {
  private factory = inject(ODataServiceFactory);

  certifiableSystemsService: ODataEntitySetService<CertifiableSystem>;
  
  constructor() {
    this.certifiableSystemsService = this.factory.entitySet<CertifiableSystem>(
      'CertifiableSystems',
      'QualicoatBackend.Domain.Certification'
    );
  }

  public getRows(params: IServerSideGetRowsParams) {
    const endRow = params.request.endRow || 20;
    const startRow = params.request.startRow || 0;
    // Create a fresh entities resource for each query to avoid accumulating state
    const freshEntities = this.certifiableSystemsService.entities();
    return freshEntities
      ?.query((q) => {
        q.expand('ProductionSite($expand=Customer($expand=AssignedGl,AssignedLaboratory)),SupplierOrManufacturerOrganisation');
        q.top(endRow);
        q.skip(startRow);
        if (params.request.sortModel?.length > 0) q.orderBy(this.convertToOrderByString(params.request.sortModel));
        if (params.request.filterModel) q.filter(this.convertToFilterString(params.request.filterModel));
      })
      .fetch({ withCount: true })
      .subscribe((response) => {
        const count = response.annots['annotations'].get('@odata.count');
        if (response.entities) {
          return params.success({ rowData: response.entities, rowCount: count });
        }
        return params.fail();
      });
  }

  private convertToOrderByString(sortModel: SortModelItem[]) {
    const customSortRules: CustomSortRule[] = [
      {
        key: 'productionSite.customer.assignedGl.name',
        fn: (_key: string, sortOrder: 'asc' | 'desc') =>
          `ProductionSite/Customer/AssignedGl/Name ${sortOrder}`,
      },
      {
        key: 'completeApprovalNumber',
        fn: (key: string, sortOrder: 'asc' | 'desc') => {
          return `prefix ${sortOrder}, number ${sortOrder}`;
        },
      },
      {
        key: 'customer.name',
        fn: (key: string, sortOrder: 'asc' | 'desc') => {
          return `ProductionSite/Customer/Name ${sortOrder}`;
        },
      },
      {
        key: 'supplierOrManufacturer',
        fn: (key: string, sortOrder: 'asc' | 'desc') => {
          return `SupplierOrManufacturerOrganisation/Name ${sortOrder}`;
        },
      },
      {
        key: 'pCode',
        fn: (_key: string, sortOrder: 'asc' | 'desc') => `${PCODE_CAST_PATH} ${sortOrder}`,
      },
    ];
    return convertToOrderByString({ sortModel, customSortRules });
  }

  private isNumeric(value: string) {
    return /^\d+$/.test(value);
  }

  private convertToFilterString(filterModel: FilterModel) {
    const customFilterRules: CustomFilterRule[] = [
      {
        key: 'customer.name',
        fn: (key: string, filterType: string, type: string, filter: string) => {
          return `contains(tolower(ProductionSite/Customer/Name), tolower('${filter}'))`;
        },
      },
      {
        key: 'completeApprovalNumber',
        fn: (key: string, filterType: string, type: string, filter: string) => {
          let prefix: string | null = null;
          let number: string | null = null;
          if (filter.includes('-')) {
            prefix = filter.split('-')[0];
            number = filter.split('-')[1];
          } else {
            if (this.isNumeric(filter)) {
              number = filter;
            } else {
              prefix = filter;
            }
          }
          const subFilters: string[] = [];
          if (type == 'contains') {
            if (prefix) subFilters.push(`(contains(tolower(prefix), tolower('${prefix}')))`);
            if (number) subFilters.push(`(contains(cast(Number, 'Edm.String'), '${number}'))`);
          } else if (type == 'equals') {
            if (prefix) subFilters.push(`(tolower(prefix) eq tolower('${prefix}'))`);
            if (number) subFilters.push(`(Number eq ${number})`);
          }
          return `(${subFilters.join(' and ')})`;
        },
      },
      {
        key: 'supplierOrManufacturer',
        fn: (key: string, filterType: string, type: string, filter: string) => {
          return `contains(tolower(SupplierOrManufacturerOrganisation/Name), tolower('${filter}'))`;
        },
      },
      {
        key: 'pCode',
        fn: (key: string, filterType: string, type: string, filter: string) => {
          if (type === 'equals') {
            return `(tolower(${PCODE_CAST_PATH}) eq tolower('${filter}'))`;
          }
          return `contains(tolower(${PCODE_CAST_PATH}), tolower('${filter}'))`;
        },
      },
    ];

    const setFilterRules: SetFilterRule[] = [
      {
        key: 'systemType',
        fn: (key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            subFilters.push(`SystemType eq '${value}'`);
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
      {
        key: 'productionSite.customer.assignedGl.name',
        fn: (_key: string, values: string[]) => {
          const subFilters = values.map(v => `ProductionSite/Customer/AssignedGl/Name eq '${v}'`);
          return subFilters.length > 0 ? `(${subFilters.join(' or ')})` : '';
        },
      },
      {
        key: 'approvalState',
        fn: (key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            subFilters.push(`ApprovalState eq '${value}'`);
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
    ];
    return convertToFilterString({ filterModel, setFilterRules, customFilterRules });
  }

  /**
   * Fetch a single system by ID with related entities
   */
  fetchSystem(systemId: string): Observable<CertifiableSystem> {
    return this.certifiableSystemsService
      .entity(systemId)
      .query((q) => q.expand('ProductionSite($expand=Customer($expand=AssignedGl,AssignedLaboratory)),SupplierOrManufacturerOrganisation'))
      .fetch()
      .pipe(map((response) => response.entity!));
  }
}
